import React from 'react';
import { Card, TextInput, Button, Badge, Alert, Spinner, useToast } from '@parsina/framework';
import { Gift, Check } from 'lucide-react';

import { C, CT, soft } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { fmtParseh } from '../../constants/pricing';
import { errorCode } from '../../lib/bffClient.js';
import { giftStatusKey, giftStatusVariant } from './Credits.shared.js';

// کد هدیه — redeem a campaign code, and see the ones already used.
//
// ── The order is the design, and it lives on the server ─────────────────
//
// The BFF marks the code spent in `marketing` FIRST and only then asks the wallet
// to grant. Mark-then-grant under-delivers if it breaks — recoverable, and the
// resolver releases the code back — while grant-then-mark OVER-delivers: پارسه
// out the door with the code still live, silently and unrecoverably.
//
// What that means for THIS file: nothing here retries a redemption. A failed
// redeem may have left the code marked and the grant unlanded
// (`GIFT_GRANT_FAILED`), which is the one state a human has to repair, and an
// automatic retry would either do nothing or double-grant. The student is told;
// the button does not fire again on its own.
//
// ── The field is forced LTR, in an RTL panel ────────────────────────────
//
// A gift code is a Latin token with a meaningful left-to-right order. In an RTL
// input a leading '-' or a mixed digit run REORDERS ON SCREEN while the value
// stays correct — so the student proofreads a string that is not the one being
// sent, and reads a different one back to support.
//
// Persian digits are NOT folded here. The marketing service normalises the code
// (`app/domain/gift_codes.py`), so a Persian keyboard is already handled, and a
// second normaliser in the browser would be a second place for that rule to
// drift.

export default function GiftCodeCard({ gifts, onRedeemed }) {
  const { t, formatNumber, formatDate } = useTranslation();
  const toast = useToast();

  const [value, setValue] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [preview, setPreview] = React.useState(null);
  const [done, setDone] = React.useState(null);

  /**
   * Look the code up WITHOUT spending it, once the student stops typing.
   *
   * `giftCodePreview` returns null for an unknown code rather than erroring, so a
   * typo shows «کد معتبر نیست» under the field and never an error banner over the
   * screen. It costs nothing and is not a redemption.
   */
  const checkCode = async () => {
    const code = value.trim();
    setPreview(null);
    if (!code) return;
    try {
      const found = await gifts.preview(code);
      setPreview(found);
      setError(found ? '' : 'credits.gift.notFound');
    } catch (err) {
      // A failed PREVIEW is not a failed redemption and must not read like one:
      // the student can still press «استفاده», and the server is the real check.
      console.warn('[gift] preview failed:', errorCode(err) || err?.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const code = value.trim();
    if (!code) {
      setError('credits.gift.empty');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const result = await gifts.redeem(code);
      setDone(result);
      setValue('');
      setPreview(null);
      onRedeemed?.(result);
    } catch (err) {
      const code2 = errorCode(err);
      if (code2 === 'GIFT_NOT_FOUND') {
        setError('credits.gift.notFound');
      } else if (code2 === 'GIFT_NOT_USABLE' || code2 === 'GIFT_GRANT_FAILED') {
        // The server's own message says WHICH — used, expired, revoked, or the
        // one case where the code was spent and the پارسه did not land. It is
        // shown verbatim rather than folded onto «کد معتبر نیست», because those
        // are different problems and only one of them needs support.
        setError('');
        toast.error(err?.message || t('errors.generic'));
      } else {
        console.error('[gift] redeem failed:', code2 || err?.message);
        toast.error(t('errors.generic'));
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Card style={{ border: `1.5px solid ${C.green}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              background: C.green,
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Check size={20} aria-hidden />
          </span>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
            {t('credits.gift.success', { parseh: fmtParseh(done.granted, formatNumber) })}
          </div>
        </div>
        {done.balance?.balance != null && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12.5, color: C.textSecondary }}>{t('credits.gift.newBalance')}</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: CT.accentInk }} dir="ltr">
              {fmtParseh(done.balance.balance, formatNumber)}
            </span>
          </div>
        )}
        <Button variant="outline" onClick={() => setDone(null)}>
          {t('common.actions.close')}
        </Button>
      </Card>
    );
  }

  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: soft(C.navy, 0.1),
            color: C.navy,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Gift size={18} aria-hidden />
        </span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
            {t('credits.gift.title')}
          </div>
          <div style={{ fontSize: 11.5, color: C.textMuted }}>{t('credits.gift.hint')}</div>
        </div>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <TextInput
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={checkCode}
          placeholder={t('credits.gift.placeholder')}
          // See the header: forced LTR so the code the student proofreads is the
          // code that is sent.
          dir="ltr"
          disabled={busy}
          maxLength={64}
          error={error ? t(error) : ''}
          style={{ flex: 1 }}
          data-testid="gift-code"
        />
        <Button type="submit" disabled={busy} data-testid="gift-submit">
          {busy ? <Spinner size={14} color="#fff" /> : null}
          {busy ? t('credits.gift.submitting') : t('credits.gift.submit')}
        </Button>
      </form>

      {/* What the code is worth, learned WITHOUT spending it. */}
      {preview && (
        <Alert variant="info">
          {t('credits.gift.worth', { parseh: fmtParseh(preview.parseh, formatNumber) })}
        </Alert>
      )}

      <p style={{ margin: 0, fontSize: 11, color: C.textMuted }}>{t('credits.gift.oneUse')}</p>

      {gifts.codes.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.textPrimary, marginBottom: 8 }}>
            {t('credits.gift.myCodes')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {gifts.codes.map((code) => (
              <div
                key={code.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  padding: '6px 0',
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                {/* AS PRINTED — what the SMS said, and what the student will read
                    back to support. Never re-cased, never re-formatted. */}
                <span style={{ fontSize: 12.5, fontWeight: 700, color: C.textPrimary }} dir="ltr">
                  <span style={{ unicodeBidi: 'isolate' }}>{code.code}</span>
                </span>
                <Badge variant={giftStatusVariant(code.status)}>{t(giftStatusKey(code.status))}</Badge>
                <span style={{ fontSize: 11.5, color: CT.ink2 }}>
                  {t('credits.gift.worth', { parseh: fmtParseh(code.parseh, formatNumber) })}
                </span>
                {code.redeemedAtMs && (
                  <span style={{ marginInlineStart: 'auto', fontSize: 11, color: C.textMuted }}>
                    {formatDate(code.redeemedAtMs, 'numeric')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
